import {
  NODE_LOCK_LABELS,
  UNIT_REQUIRES_NODE,
  UNIT_SLUGS,
  recipeLineKey,
} from "./unitDisplay";

const COMBAT_STAT_LABELS = {
  hp: "HP",
  atk: "ATK",
  def: "DEF",
  spd: "SPD",
  crit: "CRIT",
};

function isSiegeCard(card) {
  return card?.sub_type === "gate";
}

function recipeSortIndex(line) {
  const key = recipeLineKey(line);
  const index = UNIT_SLUGS.indexOf(key);
  return index < 0 ? UNIT_SLUGS.length : index;
}

function sortRecipeLines(lines) {
  return [...(lines || [])].sort((a, b) => recipeSortIndex(a) - recipeSortIndex(b));
}

function catalogAmount(line) {
  if (line?.amount != null && Number.isFinite(Number(line.amount))) {
    return Number(line.amount);
  }
  return Number(line?.min) || 0;
}

function mergeMissingCatalogRecipes(instanceLines, catalogLines) {
  const out = [...(instanceLines || [])];
  const have = new Set();
  for (const line of out) {
    const key = recipeLineKey(line);
    if (key) have.add(key);
  }
  for (const line of catalogLines || []) {
    const key = recipeLineKey(line);
    if (!key || have.has(key)) continue;
    if (line.unit) {
      const rolled = { unit: line.unit, amount: 1 };
      if (line.recipe) rolled.recipe = line.recipe;
      if (line.requires_node) rolled.requires_node = line.requires_node;
      out.push(rolled);
    } else if (line.resource) {
      const rolled = { resource: line.resource, amount: catalogAmount(line) };
      if (line.recipe) rolled.recipe = line.recipe;
      if (line.requires_node) rolled.requires_node = line.requires_node;
      out.push(rolled);
    }
  }
  return sortRecipeLines(out);
}

function produceEntries(card, instance) {
  const catalog = card?.economics?.produces || [];
  if (instance?.produces?.length) {
    return mergeMissingCatalogRecipes(instance.produces, catalog);
  }
  return sortRecipeLines(catalog);
}

function consumeEntries(card, instance) {
  const catalog = card?.economics?.consumes || [];
  if (instance?.consumes?.length) {
    return mergeMissingCatalogRecipes(instance.consumes, catalog);
  }
  return sortRecipeLines(catalog);
}

function lineAmount(line) {
  if (line?.amount != null && Number.isFinite(Number(line.amount))) {
    return Number(line.amount);
  }
  if (line?.min != null || line?.max != null) {
    const min = Number(line.min) || 1;
    const max = Number(line.max) || 4;
    return min === max ? min : `${min}–${max}`;
  }
  return 0;
}

function nodeKeyForLine(line) {
  if (line?.requires_node) return String(line.requires_node);
  const recipe = recipeLineKey(line);
  return recipe ? UNIT_REQUIRES_NODE[recipe] || null : null;
}

export function snapshotToBaseStats(instance) {
  const stats = {};
  for (const line of instance?.produces || []) {
    if (line?.unit) {
      const amount = lineAmount(line);
      if (typeof amount === "string") {
        stats[line.unit] = amount;
      } else if (amount) {
        stats[line.unit] = (Number(stats[line.unit]) || 0) + amount;
      }
      continue;
    }
    if (!line?.resource) continue;
    stats[line.resource] =
      (stats[line.resource] || 0) + (Number(line.amount) || 0);
  }
  for (const line of instance?.consumes || []) {
    if (!line?.resource) continue;
    stats[line.resource] =
      (stats[line.resource] || 0) - (Number(line.amount) || 0);
  }
  return stats;
}

const COMBAT_STAT_ORDER = ["hp", "atk", "def", "spd", "crit"];

function combatStatsFromCard(card) {
  const instanceStats = card?.instance_data?.stats;
  const catalogStats = card?.stats && typeof card.stats === "object" ? card.stats : {};
  const stats =
    instanceStats && typeof instanceStats === "object"
      ? instanceStats
      : catalogStats;
  const out = stats && typeof stats === "object" ? { ...stats } : {};
  if (card?.type === "hero") {
    out.spd = Math.max(Number(out.spd) || 0, Number(catalogStats.spd) || 0);
  }
  return out;
}

export function formatCombatStatItems(card) {
  const stats = combatStatsFromCard(card);
  const items = [];
  for (const key of COMBAT_STAT_ORDER) {
    const amount = Number(stats[key]);
    if (!Number.isFinite(amount) || amount === 0) continue;
    const label = COMBAT_STAT_LABELS[key] || key;
    const suffix = key === "crit" ? "%" : "";
    items.push({
      key: `combat-${key}`,
      text: `${label} ${amount}${suffix}`,
      dimmed: false,
    });
  }
  return items;
}

function usesCombatStatItems(card) {
  return (
    card?.type === "hero" ||
    card?.type === "equipment" ||
    isSiegeCard(card)
  );
}

export function formatSocketStatItems(card, options) {
  if (usesCombatStatItems(card)) return formatCombatStatItems(card);
  return formatBuildingStatItems(card, options);
}

function formatCombatStatLines(card) {
  const lines = [];
  const stats = combatStatsFromCard(card);
  for (const [key, value] of Object.entries(stats)) {
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount === 0) continue;
    const label = COMBAT_STAT_LABELS[key] || key;
    const sign = amount > 0 ? "+" : "";
    const suffix = key === "crit" ? "%" : "";
    lines.push(`${sign}${amount}${suffix} ${label}`);
  }
  const affixes = card?.instance_data?.affixes || card?.affixes || [];
  for (const affix of affixes) {
    const name = affix?.name || (affix?.key ? String(affix.key).replace(/_/g, " ") : "");
    if (name) lines.push(name);
  }
  return lines;
}

function formatAmountLine(amount, label) {
  if (typeof amount === "string") return `+${amount} ${label}`;
  const n = Number(amount);
  if (!n) return null;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n} ${label}`;
}

export function formatBuildingStatItems(card, { unlockedNodes } = {}) {
  if (isSiegeCard(card)) {
    return formatCombatStatLines(card).map((text, index) => ({
      key: `siege-${index}`,
      text,
      dimmed: false,
    }));
  }
  const instance = card?.instance_data || {};
  const unlocked =
    unlockedNodes == null
      ? null
      : new Set(
          (Array.isArray(unlockedNodes) ? unlockedNodes : []).map((key) =>
            String(key)
          )
        );
  const items = [];

  for (const line of produceEntries(card, instance)) {
    const label = line.unit || line.resource;
    if (!label) continue;
    const text = formatAmountLine(lineAmount(line), label);
    if (!text) continue;
    const nodeKey = line.unit ? nodeKeyForLine(line) : null;
    const nodeOk = !unlocked || !nodeKey || unlocked.has(nodeKey);
    items.push({
      key: `produce-${label}-${items.length}`,
      text,
      dimmed: !nodeOk,
      title: nodeOk
        ? undefined
        : `Requires ${NODE_LOCK_LABELS[nodeKey] || nodeKey}`,
    });
  }

  const consumeTotals = {};
  for (const line of consumeEntries(card, instance)) {
    if (!line?.resource) continue;
    if (unlocked) {
      const nodeKey = nodeKeyForLine(line);
      if (nodeKey && !unlocked.has(nodeKey)) continue;
    }
    const raw = lineAmount(line);
    const amount = typeof raw === "string" ? 0 : Number(raw);
    if (!Number.isFinite(amount) || !amount) continue;
    consumeTotals[line.resource] = (consumeTotals[line.resource] || 0) - Math.abs(amount);
  }
  for (const [resource, amount] of Object.entries(consumeTotals)) {
    const text = formatAmountLine(amount, resource);
    if (!text) continue;
    items.push({
      key: `consume-${resource}`,
      text,
      dimmed: false,
    });
  }

  const affixes = instance.affixes || [];
  for (const affix of affixes) {
    if (!affix?.key) continue;
    if (affix.type !== "special") continue;
    items.push({
      key: `affix-${affix.key}`,
      text: affix.key.replace(/_/g, " "),
      dimmed: false,
    });
  }

  if (items.length) return items;

  const stats = card?.base_stats;
  if (stats && typeof stats === "object") {
    for (const [key, value] of Object.entries(stats)) {
      const text =
        typeof value === "string"
          ? `+${value} ${key}`
          : formatAmountLine(value, key);
      if (!text) continue;
      items.push({ key: `stat-${key}`, text, dimmed: false });
    }
  }
  return items;
}

export function formatCardStatLines(card, options) {
  if (
    card?.type === "hero" ||
    card?.type === "equipment" ||
    isSiegeCard(card)
  ) {
    return formatCombatStatLines(card);
  }
  return formatBuildingStatItems(card, options).map((item) => item.text);
}
